import axios from 'axios';
import { getCookie } from '../utils/cookies';
import * as Sentry from "@sentry/react";

const API_URL = import.meta.env.VITE_ROOT_API_URL;

// Custom error class for API errors
class ApiRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for handling auth tokens
apiClient.interceptors.request.use((config) => {
  const token = getCookie('token');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Create a proper error with stack trace
      const apiError = new ApiRequestError(
        `API Error: ${error.config.method?.toUpperCase()} ${error.config.url} returned ${error.response.status}`
      );
      
      // Capture with better context
      Sentry.captureException(apiError, {
        extra: {
          url: error.config.url,
          method: error.config.method,
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          originalError: error.message
        },
        fingerprint: [
          '{{ default }}',
          String(error.response.status),
          error.config.url.split('?')[0], // Base URL without query params
        ],
      });
    }
    return Promise.reject(error);
  }
);

// Export the axios instance type for use in other files
export type ApiClient = typeof apiClient;